import React from 'react';
import { ChevronLeft, ChevronRight, Users, Home, Phone } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

export const CalendarTab = ({
  calendarMonth, setCalendarMonth, selectedDate, setSelectedDate,
  calendarBookings, calendarDateDetail, fetchCalendarDateDetail, getBookingsForDate
}) => {
  return (
    <div data-testid="calendar-content">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#FFFFFF] p-6 rounded-lg border border-[#2F5233]/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-semibold text-[#1C2A20]">{format(calendarMonth, 'MMMM yyyy')}</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}><ChevronLeft size={16} /></Button>
              <Button variant="outline" size="icon" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}><ChevronRight size={16} /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (<div key={day} className="text-center text-sm font-medium text-[#5E6E60] py-2">{day}</div>))}
            {eachDayOfInterval({ start: startOfMonth(calendarMonth), end: endOfMonth(calendarMonth) }).map((day, index) => {
              const dayBookings = getBookingsForDate(day);
              const isSelected = isSameDay(day, selectedDate);
              const firstDayOfMonth = startOfMonth(calendarMonth).getDay();
              const emptyDays = index === 0 ? Array(firstDayOfMonth).fill(null) : [];
              return (
                <React.Fragment key={day.toISOString()}>
                  {emptyDays.map((_, i) => (<div key={`empty-${i}`} className="h-20" />))}
                  <button onClick={() => { setSelectedDate(day); fetchCalendarDateDetail(day); }} className={`h-20 p-1 border rounded-lg text-left transition-colors ${isSelected ? 'border-[#2F5233] bg-[#2F5233]/5' : 'border-[#2F5233]/30 hover:border-[#2F5233]/60'}`}>
                    <span className={`text-sm font-medium ${isSelected ? 'text-[#2F5233]' : 'text-[#1C2A20]'}`}>{format(day, 'd')}</span>
                    <div className="mt-1 space-y-0.5">
                      {dayBookings.slice(0, 2).map(b => {
                        const displayName = (b.room_names && b.room_names.length > 0) ? b.room_names.join(', ') : (b.room_name || b.guest_name);
                        return (<div key={b.id} className={`text-xs truncate px-1 rounded ${b.status === 'confirmed' ? 'bg-emerald-900/30 text-emerald-300' : b.status === 'on_hold' ? 'bg-yellow-900/30 text-yellow-300' : b.status === 'pending' ? 'bg-[#2F5233]/15 text-[#2F5233]' : 'bg-red-900/30 text-red-300'}`}>{displayName}</div>);
                      })}
                      {dayBookings.length > 2 && <p className="text-xs text-[#5E6E60]">+{dayBookings.length - 2} more</p>}
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-[#FFFFFF] p-6 rounded-lg border border-[#2F5233]/30">
            <h3 className="font-serif text-lg font-semibold text-[#1C2A20] mb-4">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</h3>
            {calendarDateDetail ? (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-sm text-[#1C2A20] mb-2 flex items-center gap-2"><Users size={16} className="text-[#2F5233]" /> Booked ({calendarDateDetail.bookings.length})</h4>
                  {calendarDateDetail.bookings.length === 0 ? (
                    <p className="text-[#5E6E60] text-sm pl-6">No bookings for this date</p>
                  ) : (
                    <div className="space-y-2">
                      {calendarDateDetail.bookings.map(booking => (
                        <div key={booking.id} className="p-3 bg-[#EEF1EA] rounded-lg">
                          <div className="flex justify-between items-start mb-1">
                            <h5 className="font-medium text-sm text-[#1C2A20]">{booking.guest_name}</h5>
                            <span className={`status-badge status-${booking.status}`}>{booking.status}</span>
                          </div>
                          <p className="text-xs text-[#2F5233] font-medium">{(booking.room_names && booking.room_names.length > 0) ? booking.room_names.join(', ') : (booking.room_name || booking.room_type)}</p>
                          <div className="flex justify-between text-xs text-[#5E6E60] mt-1">
                            <span>{booking.num_guests} guests</span>
                            <span>{fmtDate(booking.check_in_date)} - {fmtDate(booking.check_out_date)}</span>
                          </div>
                          {booking.phone && <p className="text-xs text-[#5E6E60] mt-1 flex items-center gap-1"><Phone size={10} />{booking.phone}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t border-[#2F5233]/30 pt-4">
                  <h4 className="font-medium text-sm text-[#1C2A20] mb-2 flex items-center gap-2"><Home size={16} className="text-green-600" /> Available Rooms ({calendarDateDetail.available_rooms.length})</h4>
                  {calendarDateDetail.available_rooms.length === 0 ? (
                    <p className="text-[#5E6E60] text-sm pl-6">All rooms booked</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {calendarDateDetail.available_rooms.map(room => (
                        <div key={room.name} className="p-2 bg-green-50 rounded border border-green-200 text-xs">
                          <p className="font-medium text-green-800">{room.name}</p>
                          <p className="text-green-600 capitalize">{room.room_type} - {room.capacity} pax</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                {getBookingsForDate(selectedDate).length === 0 ? <p className="text-[#5E6E60] text-sm">Click a date to see details</p> : (
                  <div className="space-y-3">
                    {getBookingsForDate(selectedDate).map(booking => (
                      <div key={booking.id} className="p-3 bg-[#EEF1EA] rounded-lg">
                        <div className="flex justify-between items-start mb-1"><h4 className="font-medium text-[#1C2A20]">{booking.guest_name}</h4><span className={`status-badge status-${booking.status}`}>{booking.status}</span></div>
                        <p className="text-sm text-[#2F5233] font-medium">{(booking.room_names && booking.room_names.length > 0) ? booking.room_names.join(', ') : (booking.room_name || booking.room_type)}</p>
                        <p className="text-sm text-[#5E6E60]">{booking.num_guests} guests</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
